/**
 * Authentication hook for Backoffice
 * @implements US-099 - Backoffice Dashboard View
 * @implements FS-001.2.1 - OAuth Authentication
 * @implements FS-001.2.3 - Development Authentication
 *
 * In development mode (NODE_ENV=development), attempts dev auth first.
 * In production/staging, checks for existing session via /auth/me endpoint.
 * OAuth login is handled via redirect to /auth/github or /auth/google.
 */

import { useEffect, useState, useCallback } from 'react';

interface AuthState {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether authentication is in progress */
  isLoading: boolean;
  /** Authentication error, if any */
  error: string | null;
  /** Whether dev auth is available (development mode only) */
  isDevAuthAvailable: boolean;
}

/**
 * Check if we're in development mode
 */
const isDevelopment = import.meta.env.DEV;

/**
 * Authentication hook that supports both dev auth and OAuth.
 *
 * - In development: tries dev auth first, falls back to session check
 * - In production/staging: checks for existing session via /auth/me
 *
 * OAuth login is handled by redirecting to /auth/github or /auth/google,
 * which sets the session cookie. After redirect back, this hook will
 * detect the session.
 */
export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    error: null,
    isDevAuthAvailable: false,
  });

  /**
   * Check if user has an active session
   */
  const checkSession = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch('/auth/me', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        return !!data.user;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  /**
   * Attempt dev auth (development mode only)
   */
  const tryDevAuth = useCallback(async (): Promise<boolean> => {
    try {
      // First check if dev auth is available
      const statusResponse = await fetch('/auth/dev/status');
      if (!statusResponse.ok) {
        return false;
      }

      const statusData = await statusResponse.json();
      if (!statusData.available) {
        return false;
      }

      // Dev auth is available, try to authenticate
      const response = await fetch('/auth/dev', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: 'dev-admin@jared.local',
          role: 'product_owner',
        }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      if (data.token) {
        localStorage.setItem('authToken', data.token);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    const authenticate = async () => {
      // First check for existing session
      const hasSession = await checkSession();
      if (hasSession) {
        setState({
          isAuthenticated: true,
          isLoading: false,
          error: null,
          isDevAuthAvailable: false,
        });
        return;
      }

      // In development, try dev auth
      if (isDevelopment) {
        const devAuthSuccess = await tryDevAuth();
        if (devAuthSuccess) {
          setState({
            isAuthenticated: true,
            isLoading: false,
            error: null,
            isDevAuthAvailable: true,
          });
          return;
        }
      }

      // No session and no dev auth available - user needs to log in via OAuth
      setState({
        isAuthenticated: false,
        isLoading: false,
        error: null,
        isDevAuthAvailable: false,
      });
    };

    authenticate();
  }, [checkSession, tryDevAuth]);

  return state;
}
